import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import * as Yup from 'yup';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import Text from '../../../../shared/components/Text/Text';
import Input from '../../../../shared/_TBComponents/Input/index';
import Spinner from '../../../../shared/components/Spinner/Spinner';
import Iframely from './Iframely';
import {
  setIframelyError,
  setActiveSecondLayerModal,
  uploadMedia,
} from '../../ducks/calendarActions';
import { showErrorToast } from '../../../../shared/utils/toastManager';
import { decodeVideoUrl } from '../../../../shared/utils/decodeHtmlEntities';

// Mirrors VideoAndUrlInputs' responsive width breakpoints.
const blockMinWidth = (containerWidth) => {
  if (containerWidth < 400) return '250px';
  if (containerWidth >= 528 && containerWidth < 700) return '250px';
  return '350px';
};

const BlockContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: ${(props) => blockMinWidth(props.containerWidth)};
  ${(props) => (props.containerWidth >= 700 && 'max-width: 350px')};
`;

const Label = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 5px;
  color: #444444;
`;

// Wraps the paste + upload controls so the two entry points read as one
// unified control rather than two loose fields.
const ContainingBox = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border: 1px solid #EEEEEE;
  border-radius: 8px;
  background: #FFFFFF;
`;

const FieldLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 5px;
  color: #444444;
`;

const UploadSection = styled('div')`
  display: flex;
  flex-direction: column;
`;

const PreviewBox = styled('div')`
  display: flex;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  height: 250px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #FCFCFC;
  overflow: hidden;
  position: relative;
`;

const DropZone = styled('div')`
  position: relative;
  border: 1px dashed #77A7FF;
  cursor: pointer;
  background-color: ${(props) => (props.isDragActive ? 'white' : '#FCFCFC')};
  opacity: ${(props) => (props.isDragActive ? 0.7 : 1)};
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  height: 140px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  transition: ${TransitionGlobal};
  :hover {
    opacity: 0.7;
  }
`;

const GoldAccessLevelBadge = styled('div')`
  position: absolute;
  top: -13px;
  right: -12px;
  padding: 2px 16px 0px 16px;
  background: #FFCB47;
  border-radius: 4px;
  pointer-events: none !important;
`;

const RemoveButton = styled('button')`
  display: flex;
  width: 100%;
  height: 32px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: 2px solid #444444;
  background: transparent;
  cursor: pointer;
  user-select: none;
  transition: ${TransitionGlobal};
  :hover {
    opacity: 0.7;
  }
  :focus-visible {
    outline: 2px solid #1787FB;
    outline-offset: 2px;
  }
`;

const LinkRow = styled('a')`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: #77A7FF;
  text-decoration: none;
  overflow-wrap: break-word;
  word-break: break-word;
  :hover {
    opacity: 0.7;
  }
`;

/** Rendered when a form opts in via `unifiedVideoFlow`; kept separate from the
 * legacy VideoAndUrlInputs so this can iterate without risking that flow. */
const ExerciseVideoBlock = ({
  errors,
  touched,
  mediaType,
  videoUrlValue,
  mediaUrlValue,
  setFieldValue,
  setFieldTouched,
  setFieldError,
  containerWidth,
  isDisabled,
}) => {
  const dispatch = useDispatch();
  const iframelyError = useSelector((state) => state.calendar.data.iframelyError);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const currentUserAccessLevel = currentUser?.organizationAccessLevel;
  const accountCode = currentUser?.accountCode;
  const canUpload = currentUserAccessLevel >= 4;

  const isUrlMedia = (mediaType === 'EMBED_LINK' || mediaType === 'EXTERNAL_URL') && !!videoUrlValue;
  const isFileMedia = mediaType === 'VIDEO' && !!videoUrlValue;

  /** Seeded from the prefilled values so an existing URL exercise renders its
   * preview immediately on edit-open instead of flashing the blank state. */
  const [urlToEmbed, setUrlToEmbed] = useState(isUrlMedia ? decodeVideoUrl(videoUrlValue) : '');
  const [isUploading, setIsUploading] = useState(false);

  const urlValidationSchema = Yup.string().url('Must be a valid URL').nullable(true);

  const validateUrlAndSetUrlToEmbed = async (value) => {
    try {
      setFieldTouched('videoUrl', true);
      const validatedValue = await urlValidationSchema.validate(value);
      setUrlToEmbed(validatedValue);
    } catch (err) {
      dispatch(setIframelyError(null));
      setFieldError('videoUrl', err.message);
    }
  };

  /** Same debounce as the legacy URL input — avoid re-resolving on every keystroke. */
  useEffect(() => {
    if (!isUrlMedia && videoUrlValue) {
      const timeoutId = setTimeout(() => validateUrlAndSetUrlToEmbed(videoUrlValue), 500);
      return () => clearTimeout(timeoutId);
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrlValue]);

  /** Piggybacks on Iframely's setFieldValue('mediaId', id) call to also stamp
   * mediaType + the dirty flag, so submit knows to fire the media-association PUT. */
  const trackedSetFieldValue = useCallback((field, value) => {
    if (field === 'mediaId' && value) {
      setFieldValue('mediaType', 'EXTERNAL_URL');
      setFieldValue('videoDirty', true);
    }
    setFieldValue(field, value);
  }, [setFieldValue]);

  const clearVideo = () => {
    setFieldValue('videoUrl', '');
    setFieldTouched('videoUrl', false);
    setFieldValue('mediaUrl', null);
    setFieldValue('mediaId', null);
    setFieldValue('mediaType', '');
    setFieldValue('videoDirty', true);
    dispatch(setIframelyError(null));
    setUrlToEmbed('');
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file || !file.type.includes('video')) {
      showErrorToast('Please drop a valid video file.');
      return;
    }
    setIsUploading(true);
    dispatch(uploadMedia(accountCode, file, 'video', {
      onSuccess: (res) => {
        setFieldValue('videoUrl', res.mediaUrl);
        setFieldValue('mediaUrl', res.thumbnailUrl);
        setFieldValue('mediaId', res.id);
        setFieldValue('mediaType', 'VIDEO');
        setFieldValue('videoDirty', true);
        setIsUploading(false);
      },
      onError: (err) => {
        showErrorToast(err);
        setIsUploading(false);
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountCode]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'video/*',
    disabled: !canUpload,
  });

  // A Yup-valid URL the embed service hasn't confirmed (resolving or rejected) —
  // stays editable rather than collapsing, so a bad link can be fixed in place.
  const hasPendingUrl = !isUrlMedia && !isFileMedia && !!urlToEmbed
    && !errors.videoUrl && !!videoUrlValue;

  /** Surfaces the in-flight state so submit can hold — saving mid-resolve or
   * mid-upload would create the exercise without its video, with no error. */
  const videoPending = isUploading || (!isUrlMedia && !isFileMedia
    && !!videoUrlValue && !errors.videoUrl && !iframelyError);
  useEffect(() => {
    setFieldValue('videoPending', videoPending);
  }, [videoPending, setFieldValue]);
  // Entry states (blank or pending) keep the editable URL field as the header;
  // only a confirmed embed collapses to link + preview + Remove.
  const isEntry = !isUrlMedia && !isFileMedia && !isDisabled;
  const showRemove = (isUrlMedia || isFileMedia) && !isDisabled;

  // Kept at a stable position in the box so Iframely survives the
  // pending → confirmed transition without remounting (no re-resolve flash).
  let previewBody;
  if (isFileMedia) {
    previewBody = (
      <PreviewBox>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          controls
          poster={mediaUrlValue || undefined}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        >
          <source src={videoUrlValue} type='video/mp4' />
        </video>
      </PreviewBox>
    );
  } else if (isUrlMedia || hasPendingUrl) {
    previewBody = (
      <PreviewBox>
        <Iframely
          url={urlToEmbed}
          setFieldValue={trackedSetFieldValue}
          videoUrlValue={videoUrlValue}
          mediaType={mediaType}
          autoPlay={false}
          skipMediaUpload={isUrlMedia}
        />
      </PreviewBox>
    );
  } else if (isDisabled) {
    previewBody = (
      <PreviewBox>
        <Text fontSize='16px' color='#9E9E9E'>No Video</Text>
      </PreviewBox>
    );
  } else {
    previewBody = (
      <UploadSection>
        <FieldLabel>Or upload a video file</FieldLabel>
        {isUploading ? (
          <PreviewBox style={{ height: '140px' }}>
            <Spinner />
          </PreviewBox>
        ) : (
          <DropZone
            isDragActive={isDragActive}
            {...getRootProps({
              onClick: () => {
                if (!canUpload) dispatch(setActiveSecondLayerModal('premiumFeatureUploadVideoModal'));
              },
            })}
          >
            {!canUpload && (
              <GoldAccessLevelBadge>
                <Text fontWeight={700} fontSize='13px'>Gold</Text>
              </GoldAccessLevelBadge>
            )}
            <input {...getInputProps()} />
            <IcomoonReact
              iconSet={iconSet}
              size={24}
              icon='download'
              color='#77A7FF'
              style={{ transform: 'rotate(180deg)' }}
            />
            <Text fontSize='13px' fontWeight={700} color='#77A7FF'>Click to browse or drag and drop</Text>
          </DropZone>
        )}
      </UploadSection>
    );
  }

  return (
    <BlockContainer containerWidth={containerWidth}>
      <Label>Instructional Video</Label>
      <ContainingBox>
        {isUrlMedia && !isDisabled && (
          <LinkRow href={decodeVideoUrl(videoUrlValue)} target='_blank' rel='noreferrer'>
            <Text fontSize='13px' color='#77A7FF'>{decodeVideoUrl(videoUrlValue)}</Text>
            <IcomoonReact iconSet={iconSet} size={12} icon='link' color='#77A7FF' />
          </LinkRow>
        )}
        {isEntry && (
          <Input
            // Surface the embed service's rejection on the field itself (mirroring
            // Build) instead of silently collapsing on a non-embeddable URL.
            error={(!!touched.videoUrl && !!errors.videoUrl)
              || (!!touched.videoUrl && !!iframelyError)}
            errorMsg={errors.videoUrl}
            fontSize='16px'
            fontWeight={400}
            textColor='#444444'
            height='38px'
            label='Paste a video URL'
            onClear={clearVideo}
            name='videoUrl'
            right='12px'
            styledPropsLabel={{ fontSize: '13px', fontWeight: 700 }}
            value={videoUrlValue}
            width='100%'
            onChange={(e) => {
              const { value } = e.target;
              setFieldValue('videoUrl', value);
              // Drop the stale embed error as the user edits so the corrected
              // URL re-resolves cleanly.
              if (iframelyError) dispatch(setIframelyError(null));
            }}
            placeholder='Enter video url (ex. YouTube, Vimeo)'
          />
        )}
        {previewBody}
        {showRemove && (
          <RemoveButton type='button' onClick={clearVideo}>
            <Text fontSize='13px' fontWeight={700} color='#444444'>Remove video</Text>
          </RemoveButton>
        )}
      </ContainingBox>
    </BlockContainer>
  );
};

ExerciseVideoBlock.propTypes = {
  errors: PropTypes.shape({ videoUrl: PropTypes.string }),
  touched: PropTypes.shape({ videoUrl: PropTypes.bool }),
  mediaType: PropTypes.string,
  videoUrlValue: PropTypes.string,
  mediaUrlValue: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  setFieldError: PropTypes.func.isRequired,
  containerWidth: PropTypes.number,
  isDisabled: PropTypes.bool,
};

ExerciseVideoBlock.defaultProps = {
  errors: {},
  touched: {},
  mediaType: '',
  videoUrlValue: '',
  mediaUrlValue: '',
  containerWidth: 0,
  isDisabled: false,
};

export default ExerciseVideoBlock;
